import { useQuery } from '@tanstack/react-query';
import { ErrorText, Loading, cx } from '../components/ui';
import { errorMessage } from '../lib/api';
import { dateTime, timeAgo, timecode } from '../lib/format';
import { qk } from '../queries/keys';
import { fetchViewers } from '../queries/videos';

/**
 * Who watched this recording, for whoever owns it.
 *
 * The count on its own answers "did anyone", which is rarely the question:
 * what the owner sent it for is usually one named person seeing it. So the
 * people who were signed in are named, and how far each of them got is worth
 * more than that they opened it, since a recording opened and abandoned at
 * four seconds is not a recording that was watched.
 *
 * Everyone else stays a number. A share link is meant to work for whoever
 * holds it, and putting a face to somebody who was never asked for one would
 * be a different product than the one people are linking to.
 */
export function Viewers({ videoId, durationSec }: { videoId: string; durationSec: number | null }) {
  const query = useQuery({ queryKey: qk.viewers(videoId), queryFn: () => fetchViewers(videoId) });

  if (query.isPending) return <Loading label="Loading who watched" />;
  if (query.isError) {
    return (
      <div className="p-3">
        <ErrorText>{errorMessage(query.error)}</ErrorText>
      </div>
    );
  }

  const { people, anonymous, emails } = query.data;
  // Addresses from the gate that are not already a named account, so the
  // same person is not listed twice under two descriptions.
  const named = new Set(people.map((person) => person.email.toLowerCase()));
  const guests = emails.filter((one) => !named.has(one.email.toLowerCase()));
  const nobody = people.length === 0 && anonymous.views === 0 && guests.length === 0;

  if (nobody) {
    return (
      <p className="px-3 py-8 text-center text-caption text-chalk-faint">
        Nobody has opened this yet. The view count here counts everyone; the names are the people who were signed in
        when they watched.
      </p>
    );
  }

  const progress = (seconds: number) => {
    if (!durationSec || durationSec <= 0) return null;
    return Math.min(100, Math.round((seconds / durationSec) * 100));
  };

  return (
    <div className="space-y-4 p-1">
      {people.length > 0 ? (
        <ul className="space-y-2">
          {people.map((person) => {
            const percent = progress(person.maxProgressSec);
            return (
              <li key={person.id} className="flex items-center gap-2.5 rounded border border-hairline px-2.5 py-2">
                {person.avatarUrl ? (
                  <img src={person.avatarUrl} alt="" className="h-7 w-7 shrink-0 rounded-full object-cover" />
                ) : (
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-hairline bg-slate-800 text-caption uppercase text-chalk-dim">
                    {(person.name?.trim() || person.email).slice(0, 1)}
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-body text-chalk">{person.name?.trim() || person.email}</span>
                  <span className="block truncate text-caption text-chalk-faint" title={dateTime(person.lastSeenAt)}>
                    {timeAgo(person.lastSeenAt)}
                    {person.views > 1 ? ` · ${person.views} times` : ''}
                  </span>
                </span>
                <span className="shrink-0 text-right">
                  <span className={cx('block text-caption', person.completed ? 'text-go' : 'text-chalk-dim')}>
                    {person.completed ? 'Watched' : percent !== null ? `${percent}%` : timecode(person.maxProgressSec)}
                  </span>
                  {!person.completed && percent !== null ? (
                    <span className="mt-1 block h-1 w-14 overflow-hidden rounded-full bg-slate-800">
                      <span className="block h-full rounded-full bg-chalk-dim" style={{ width: `${percent}%` }} />
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {guests.length > 0 ? (
        <div>
          <p className="mb-1.5 text-caption text-chalk-faint">
            Gave an address to watch. Anyone can type any address, so these are not accounts.
          </p>
          <ul className="space-y-1">
            {guests.map((guest) => (
              <li key={guest.email} className="flex items-baseline justify-between gap-3 text-caption">
                <span className="min-w-0 truncate text-chalk-dim">{guest.email}</span>
                <span className="shrink-0 text-chalk-faint" title={dateTime(guest.createdAt)}>
                  {timeAgo(guest.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {anonymous.views > 0 ? (
        <p className="text-caption text-chalk-faint">
          {anonymous.views === 1 ? '1 view' : `${anonymous.views} views`} from{' '}
          {anonymous.browsers === 1 ? 'someone' : `${anonymous.browsers} people`} who were not signed in
          {anonymous.completed > 0 ? `, ${anonymous.completed} of them to the end` : ''}.
        </p>
      ) : null}
    </div>
  );
}
